/**
 * Tracés posés sur un graphique : niveaux, segments, mesures.
 *
 * Ils vivent dans le navigateur, par paire. C'est un choix assumé : un tracé
 * est une note de travail personnelle, pas une donnée de compte. Le serveur
 * n'a pas à connaître les lignes que vous dessinez, et les faire transiter
 * ajouterait une latence à chaque trait.
 *
 * Conséquence à connaître : ils ne suivent pas d'un appareil à l'autre. Le
 * jour où ce sera gênant, la structure est prête à être envoyée telle quelle.
 */

/** Ancrage d'un point : un instant et un prix, jamais des pixels. */
export interface DrawingPoint {
  /** Horodatage Unix en secondes, comme les bougies. */
  time: number
  price: number
}

export type DrawingKind = 'level' | 'segment' | 'ruler'

export interface Drawing {
  id: string
  kind: DrawingKind
  color: string
  /**
   * Un point pour un niveau horizontal, deux pour un segment ou une mesure.
   *
   * Un niveau ne retient que son prix — il traverse tout le graphique — mais
   * garde quand même son instant de création, ce qui permettra plus tard de
   * le limiter à une plage sans changer le format.
   */
  points: DrawingPoint[]
}

/** Palette proposée. Des teintes distinctes sur fond sombre, sans fluo. */
export const DRAWING_COLORS = [
  '#2962ff', // bleu, la couleur d'accent de l'application
  '#f5a623', // orange
  '#26a69a', // vert
  '#ef5350', // rouge
  '#ab47bc', // violet
  '#d1d4dc', // gris clair
] as const

const PREFIX = 'reversetrader.drawings.'

/**
 * Les tracés sont rangés par paire.
 *
 * Pas par intervalle : une résistance à 1,1650 est la même en M15 et en H4.
 * Les cloisonner par intervalle obligerait à la retracer six fois.
 */
export function loadDrawings(symbol: string): Drawing[] {
  try {
    const raw = window.localStorage.getItem(PREFIX + symbol)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Drawing[]) : []
  } catch {
    // Un stockage illisible — quota, mode privé, données corrompues — ne doit
    // pas empêcher le graphique de s'afficher.
    return []
  }
}

export function saveDrawings(symbol: string, drawings: Drawing[]): void {
  try {
    window.localStorage.setItem(PREFIX + symbol, JSON.stringify(drawings))
  } catch {
    /* Rien à faire d'utile : mieux vaut perdre un tracé qu'une session. */
  }
}

export function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Distance en pips entre deux prix.
 *
 * `pipSize` vient du courtier — 0,0001 sur EURUSD, 0,01 sur USDJPY. Le coder
 * en dur donnerait des mesures cent fois fausses sur les paires en yen.
 */
export function pipsBetween(a: number, b: number, pipSize: number): number {
  if (!pipSize) return 0
  return Math.abs(a - b) / pipSize
}
